/**
 * WARM SUCCESS DESIGN SYSTEM
 *
 * Direction: Chaleureux, accessible, humain
 * Inspiration: Notion, Calm, Headspace
 * Mood: Confiance, Accompagnement, Réussite
 *
 * This file defines all design tokens for the Warm Success theme
 * to ensure consistency across the entire application.
 */

import { alpha } from '@mui/material/styles';

// =============================================================================
// COLOR PALETTE
// =============================================================================

/**
 * Primary Colors - Forest Green
 * Used for: Primary actions, links, focus states, success indicators
 */
export const warmSuccessPrimary = {
  50: '#F0FDF4',   // Très léger - backgrounds subtils
  100: '#DCFCE7',  // Léger - hover backgrounds
  200: '#BBF7D0',  // Clair - borders, dividers
  300: '#86EFAC',  // Moyen clair - disabled states
  400: '#4ADE80',  // Moyen - secondary buttons
  500: '#22C55E',  // Principal clair - success states
  600: '#16A34A',  // Principal - buttons, links (light mode)
  700: '#166534',  // Principal foncé - text, headers (main)
  800: '#14532D',  // Foncé - dark mode primary
  900: '#052E16',  // Très foncé - dark mode backgrounds
} as const;

/**
 * Secondary Colors - Warm Amber/Orange
 * Used for: Accents, warnings, highlights, CTAs
 */
export const warmSuccessSecondary = {
  50: '#FFFBEB',   // Très léger - backgrounds
  100: '#FEF3C7',  // Léger - hover
  200: '#FDE68A',  // Clair - badges
  300: '#FCD34D',  // Moyen clair - stars, ratings
  400: '#FBBF24',  // Moyen - highlights
  500: '#F59E0B',  // Principal - warnings, accents (main)
  600: '#D97706',  // Principal foncé - hover states
  700: '#B45309',  // Foncé - text accents
  800: '#92400E',  // Très foncé - terracotta
  900: '#78350F',  // Presque noir - dark accents
} as const;

/**
 * Accent Colors - Warm Orange/Terracotta
 * Used for: Important CTAs, notifications, emphasis
 */
export const warmSuccessAccent = {
  50: '#FFF7ED',
  100: '#FFEDD5',
  200: '#FED7AA',
  300: '#FDBA74',
  400: '#FB923C',
  500: '#F97316',  // Main accent
  600: '#EA580C',  // Dark accent (main for CTAs)
  700: '#C2410C',
  800: '#9A3412',
  900: '#7C2D12',
} as const;

/**
 * Neutral Colors - Warm Stone
 * Used for: Text, backgrounds, borders, surfaces
 */
export const warmSuccessNeutral = {
  50: '#FAFAF9',   // Background principale
  100: '#F5F5F4',  // Surface alternative
  200: '#E7E5E4',  // Borders
  300: '#D6D3D1',  // Dividers
  400: '#A8A29E',  // Placeholder text
  500: '#78716C',  // Secondary text
  600: '#57534E',  // Muted text
  700: '#44403C',  // Primary text (light mode)
  800: '#292524',  // Headers
  900: '#1C1917',  // Main text color
} as const;

/**
 * Background Colors
 */
export const warmSuccessBackground = {
  default: '#FFFBF5',      // Crème chaud - main background
  paper: '#FFFFFF',        // Blanc pur - cards, modals
  elevated: '#FFFAF3',     // Slightly elevated surfaces
  subtle: '#FFF7ED',       // Subtle background for sections
  muted: '#F5F5F4',        // Muted sections
} as const;

/**
 * Semantic Colors
 */
export const warmSuccessSemantic = {
  success: {
    light: '#86EFAC',
    main: '#22C55E',
    dark: '#166534',
    contrastText: '#FFFFFF',
  },
  warning: {
    light: '#FDE68A',
    main: '#F59E0B',
    dark: '#B45309',
    contrastText: '#1C1917',
  },
  error: {
    light: '#FECACA',
    main: '#EF4444',
    dark: '#B91C1C',
    contrastText: '#FFFFFF',
  },
  info: {
    light: '#BFDBFE',
    main: '#3B82F6',
    dark: '#1D4ED8',
    contrastText: '#FFFFFF',
  },
} as const;

// =============================================================================
// TYPOGRAPHY
// =============================================================================

/**
 * Font Families
 * Primary: Inter - Clean, modern, highly readable
 * Display: Optional serif for headings (if desired)
 */
export const warmSuccessTypography = {
  fontFamily: {
    primary: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    display: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    mono: '"JetBrains Mono", "Fira Code", Consolas, monospace',
  },

  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  fontSize: {
    xs: '0.75rem',     // 12px
    sm: '0.875rem',    // 14px
    base: '1rem',      // 16px
    lg: '1.125rem',    // 18px
    xl: '1.25rem',     // 20px
    '2xl': '1.5rem',   // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem',  // 36px
    '5xl': '3rem',     // 48px
    '6xl': '3.75rem',  // 60px
  },

  lineHeight: {
    tight: 1.15,
    snug: 1.3,
    normal: 1.5,
    relaxed: 1.7,
    loose: 1.8,
  },

  letterSpacing: {
    tight: '-0.02em',
    normal: '0',
    wide: '0.02em',
  },
} as const;

// =============================================================================
// SPACING (8px Grid System)
// =============================================================================

export const warmSuccessSpacing = {
  // Base unit: 8px
  unit: 8,

  // Named spacing
  none: 0,
  xs: 4,      // 0.5 units
  sm: 8,      // 1 unit
  md: 16,     // 2 units
  lg: 24,     // 3 units
  xl: 32,     // 4 units
  '2xl': 48,  // 6 units
  '3xl': 64,  // 8 units
  '4xl': 96,  // 12 units
  '5xl': 128, // 16 units

  // Component-specific
  cardPadding: 24,         // Cards internal padding
  sectionPadding: 64,      // Section vertical padding
  containerPadding: 24,    // Container horizontal padding
  inputPadding: 12,        // Input internal padding
  buttonPaddingX: 24,      // Button horizontal padding
  buttonPaddingY: 12,      // Button vertical padding

  // Responsive section padding
  sectionPaddingMobile: 48,
  sectionPaddingDesktop: 80,
} as const;

// =============================================================================
// BORDER RADIUS
// =============================================================================

export const warmSuccessBorderRadius = {
  none: 0,
  sm: 4,       // Small elements, chips
  md: 8,       // Buttons, inputs
  lg: 12,      // Cards, modals (default)
  xl: 16,      // Large cards, sections
  '2xl': 24,   // Extra large elements
  '3xl': 32,   // Feature cards
  full: 9999,  // Pills, avatars
} as const;

// =============================================================================
// SHADOWS
// =============================================================================

export const warmSuccessShadows = {
  none: 'none',

  // Subtle shadows for elevated surfaces
  sm: '0 1px 2px 0 rgba(28, 25, 23, 0.05)',

  // Default card shadow
  md: '0 4px 6px -1px rgba(28, 25, 23, 0.07), 0 2px 4px -2px rgba(28, 25, 23, 0.05)',

  // Elevated elements (dropdowns, popovers)
  lg: '0 10px 15px -3px rgba(28, 25, 23, 0.08), 0 4px 6px -4px rgba(28, 25, 23, 0.05)',

  // Modals, drawers
  xl: '0 20px 25px -5px rgba(28, 25, 23, 0.1), 0 8px 10px -6px rgba(28, 25, 23, 0.05)',

  // Maximum elevation
  '2xl': '0 25px 50px -12px rgba(28, 25, 23, 0.15)',

  // Colored shadows for primary elements
  primary: `0 8px 24px ${alpha('#166534', 0.25)}`,
  primaryHover: `0 12px 32px ${alpha('#166534', 0.35)}`,

  // Focus rings
  focusRing: `0 0 0 3px ${alpha('#22C55E', 0.4)}`,

  // Inner shadows for inputs
  inner: 'inset 0 2px 4px 0 rgba(28, 25, 23, 0.05)',
} as const;

// =============================================================================
// TRANSITIONS & ANIMATIONS
// =============================================================================

export const warmSuccessTransitions = {
  // Easing functions
  easing: {
    default: 'cubic-bezier(0.4, 0, 0.2, 1)',     // Standard
    in: 'cubic-bezier(0.4, 0, 1, 1)',            // Accelerate
    out: 'cubic-bezier(0, 0, 0.2, 1)',           // Decelerate
    inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',       // Standard
    bounce: 'cubic-bezier(0.34, 1.56, 0.64, 1)', // Playful bounce
  },

  // Durations
  duration: {
    instant: 50,
    fast: 150,
    normal: 250,
    slow: 400,
    slower: 600,
  },

  // Pre-composed transitions
  fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
  normal: '250ms cubic-bezier(0.4, 0, 0.2, 1)',
  slow: '400ms cubic-bezier(0.4, 0, 0.2, 1)',

  // Component-specific
  button: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
  card: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
  modal: 'all 300ms cubic-bezier(0.16, 1, 0.3, 1)',
  tooltip: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
} as const;

// Framer Motion variants
export const warmSuccessMotionVariants = {
  fadeIn: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.3 },
  },

  fadeInUp: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 20 },
    transition: { duration: 0.4, ease: [0.4, 0, 0.2, 1] },
  },

  fadeInDown: {
    initial: { opacity: 0, y: -20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
    transition: { duration: 0.4, ease: [0.4, 0, 0.2, 1] },
  },

  scale: {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
    transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
  },

  slideInRight: {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 },
    transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
  },

  stagger: {
    animate: {
      transition: {
        staggerChildren: 0.08,
      },
    },
  },

  staggerItem: {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.3 },
  },
} as const;

// =============================================================================
// Z-INDEX LAYERS
// =============================================================================

export const warmSuccessZIndex = {
  hide: -1,
  base: 0,
  dropdown: 1000,
  sticky: 1100,
  modalBackdrop: 1200,
  modal: 1300,
  popover: 1400,
  tooltip: 1500,
  toast: 1600,
} as const;

// =============================================================================
// COMPONENT STYLES
// =============================================================================

/**
 * Button Variants
 */
export const warmSuccessButtonStyles = {
  // Primary button (filled)
  primary: {
    backgroundColor: warmSuccessPrimary[700],
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: warmSuccessPrimary[600],
      transform: 'translateY(-2px)',
      boxShadow: warmSuccessShadows.primaryHover,
    },
    '&:active': {
      transform: 'translateY(0)',
    },
    '&:focus-visible': {
      boxShadow: warmSuccessShadows.focusRing,
    },
  },

  // Secondary button (outlined)
  secondary: {
    backgroundColor: 'transparent',
    color: warmSuccessPrimary[700],
    border: `1px solid ${warmSuccessNeutral[200]}`,
    '&:hover': {
      backgroundColor: alpha(warmSuccessPrimary[700], 0.05),
      borderColor: warmSuccessPrimary[700],
    },
  },

  // Ghost button (text only)
  ghost: {
    backgroundColor: 'transparent',
    color: warmSuccessNeutral[600],
    '&:hover': {
      backgroundColor: warmSuccessNeutral[100],
      color: warmSuccessNeutral[900],
    },
  },

  // Accent button (CTA)
  accent: {
    backgroundColor: warmSuccessAccent[600],
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: warmSuccessAccent[700],
      transform: 'translateY(-2px)',
    },
  },
} as const;

/**
 * Card Variants
 */
export const warmSuccessCardStyles = {
  // Default card
  default: {
    backgroundColor: warmSuccessBackground.paper,
    borderRadius: warmSuccessBorderRadius.lg,
    border: `1px solid ${warmSuccessNeutral[200]}`,
    boxShadow: 'none',
    transition: warmSuccessTransitions.card,
  },

  // Elevated card (with shadow)
  elevated: {
    backgroundColor: warmSuccessBackground.paper,
    borderRadius: warmSuccessBorderRadius.lg,
    border: 'none',
    boxShadow: warmSuccessShadows.md,
  },

  // Interactive card (hover effects)
  interactive: {
    backgroundColor: warmSuccessBackground.paper,
    borderRadius: warmSuccessBorderRadius.lg,
    border: `1px solid ${warmSuccessNeutral[200]}`,
    boxShadow: 'none',
    cursor: 'pointer',
    transition: warmSuccessTransitions.card,
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: warmSuccessShadows.lg,
      borderColor: alpha(warmSuccessPrimary[500], 0.3),
    },
  },

  // Highlighted card (with accent border)
  highlighted: {
    backgroundColor: warmSuccessBackground.paper,
    borderRadius: warmSuccessBorderRadius.lg,
    border: `2px solid ${warmSuccessPrimary[500]}`,
    boxShadow: warmSuccessShadows.primary,
  },
} as const;

/**
 * Input Styles
 */
export const warmSuccessInputStyles = {
  default: {
    backgroundColor: warmSuccessBackground.paper,
    borderRadius: warmSuccessBorderRadius.md,
    border: `1px solid ${warmSuccessNeutral[300]}`,
    padding: `${warmSuccessSpacing.inputPadding}px ${warmSuccessSpacing.md}px`,
    transition: warmSuccessTransitions.fast,
    '&:hover': {
      borderColor: warmSuccessNeutral[400],
    },
    '&:focus': {
      borderColor: warmSuccessPrimary[500],
      boxShadow: warmSuccessShadows.focusRing,
    },
    '&::placeholder': {
      color: warmSuccessNeutral[400],
    },
  },
} as const;

/**
 * Chip/Badge Variants
 */
export const warmSuccessChipStyles = {
  default: {
    backgroundColor: warmSuccessNeutral[100],
    color: warmSuccessNeutral[700],
    borderRadius: warmSuccessBorderRadius.full,
    fontWeight: warmSuccessTypography.fontWeight.medium,
    fontSize: warmSuccessTypography.fontSize.sm,
  },

  primary: {
    backgroundColor: alpha(warmSuccessPrimary[500], 0.1),
    color: warmSuccessPrimary[700],
  },

  success: {
    backgroundColor: alpha(warmSuccessSemantic.success.main, 0.1),
    color: warmSuccessSemantic.success.dark,
  },

  warning: {
    backgroundColor: alpha(warmSuccessSemantic.warning.main, 0.1),
    color: warmSuccessSemantic.warning.dark,
  },

  error: {
    backgroundColor: alpha(warmSuccessSemantic.error.main, 0.1),
    color: warmSuccessSemantic.error.dark,
  },
} as const;

// =============================================================================
// LAYOUT CONSTANTS
// =============================================================================

export const warmSuccessLayout = {
  // Header
  headerHeight: 64,
  headerHeightMobile: 56,

  // Sidebar
  sidebarWidth: 260,
  sidebarCollapsed: 72,

  // Container max widths
  containerXs: 540,
  containerSm: 720,
  containerMd: 960,
  containerLg: 1140,
  containerXl: 1320,

  // Content area
  contentMaxWidth: 1200,
  readingWidth: 680,

  // Breakpoints
  breakpoints: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1536,
  },
} as const;

// =============================================================================
// EXPORTS - Complete Theme Object
// =============================================================================

export const warmSuccessTheme = {
  colors: {
    primary: warmSuccessPrimary,
    secondary: warmSuccessSecondary,
    accent: warmSuccessAccent,
    neutral: warmSuccessNeutral,
    background: warmSuccessBackground,
    semantic: warmSuccessSemantic,
  },
  typography: warmSuccessTypography,
  spacing: warmSuccessSpacing,
  borderRadius: warmSuccessBorderRadius,
  shadows: warmSuccessShadows,
  transitions: warmSuccessTransitions,
  motionVariants: warmSuccessMotionVariants,
  zIndex: warmSuccessZIndex,
  components: {
    button: warmSuccessButtonStyles,
    card: warmSuccessCardStyles,
    input: warmSuccessInputStyles,
    chip: warmSuccessChipStyles,
  },
  layout: warmSuccessLayout,
} as const;

// Type exports
export type WarmSuccessTheme = typeof warmSuccessTheme;
export type WarmSuccessColors = typeof warmSuccessTheme.colors;
